import {
  OrderLineCreateManyOrderInput,
  OrderLineStatus,
  OrderLineType,
} from '@/__generated__/graphql';
import { Button, NumberInput, Select, Stack, TextInput } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';

export type OrderLineInput = Pick<
  OrderLineCreateManyOrderInput,
  | 'id'
  | 'code'
  | 'name'
  | 'description'
  | 'price'
  | 'status'
  | 'type'
  | 'productVariantId'
  | 'shippingMethodId'
  | 'paymentMethodId'
>;

interface OrderLineFormProps {
  line?: OrderLineInput | null;
  onSubmit: (line: OrderLineInput) => void;
  loading?: boolean;
}

export const OrderLineForm = ({ line, onSubmit, loading }: OrderLineFormProps) => {
  const form = useForm<OrderLineInput>({
    initialValues: {
      id: line?.id,
      code: line?.code ?? '',
      name: line?.name ?? '',
      description: line?.description,
      price: line?.price ?? 0,
      status: line?.status ?? OrderLineStatus.Created,
      type: line?.type ?? OrderLineType.Custom,
      productVariantId: line?.productVariantId,
      shippingMethodId: line?.shippingMethodId,
      paymentMethodId: line?.paymentMethodId,
    },
    validate: {
      code: isNotEmpty('Kód je povinný'),
      name: isNotEmpty('Názov je povinný'),
      price: isNotEmpty('Cena nemôže byť prázdna'),
      productVariantId: (value, values) => {
        if (values.type === OrderLineType.Product && !value) {
          return 'Produkt je povinný pre produktový riadok';
        }

        return null;
      },
      shippingMethodId: (value, values) => {
        if (values.type === OrderLineType.Shipping && !value) {
          return 'Doručenie je povinné pre doručovací riadok';
        }

        return null;
      },
      paymentMethodId: (value, values) => {
        if (values.type === OrderLineType.Payment && !value) {
          return 'Platba je povinná pre platobný riadok';
        }

        return null;
      },
    },
  });

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="xs">
        <Select {...form.getInputProps('type')} label="Typ" data={Object.values(OrderLineType)} />
        {form.values.type === OrderLineType.Product && (
          <TextInput {...form.getInputProps('productVariantId')} label="Produkt variant ID" />
        )}
        {form.values.type === OrderLineType.Shipping && (
          <TextInput {...form.getInputProps('shippingMethodId')} label="Doručenie ID" />
        )}
        {form.values.type === OrderLineType.Payment && (
          <TextInput {...form.getInputProps('paymentMethodId')} label="Platba ID" />
        )}
        <TextInput {...form.getInputProps('code')} label="Kód" data-autofocus />
        <TextInput {...form.getInputProps('name')} label="Názov" />
        <TextInput {...form.getInputProps('description')} label="Popis" />
        <NumberInput
          {...form.getInputProps('price')}
          label="Cena"
          allowDecimal={false}
          hideControls={true}
        />
        <Select
          {...form.getInputProps('status')}
          label="Stav"
          data={Object.values(OrderLineStatus)}
        />
        <Button type="submit" fullWidth loading={loading}>
          Uložiť
        </Button>
      </Stack>
    </form>
  );
};
